import { User } from './user'
import { FeathersModel } from './feathersModel'
import { DateUtils } from '../services/date-utils.service'
import { createIntlComparer, DEFAULT_LOCALES } from '../services/utils'
import { ClientAllocation } from './client-allocation'

// See https://coryrylan.com/blog/rich-domain-models-with-typescript

export class Client extends FeathersModel {
  static serviceName = 'clients'
  static intlSortComparer = createIntlComparer(DEFAULT_LOCALES, {
    sensitivity: 'accent',
  })

  dependenceLevelId: number
  preferredGenderId: number // preference pohlavi pri asistenci
  keyWorkerUserId: number // flattened from active clientAllocations[0].keyWorkerUserId
  keyWorkerUser: User // flattened from active clientAllocations[0].keyWorkerUser
  benefitsCardTypeId: number
  userId: string
  childrenCount: number
  coordinatorNote: string // flattened from active clientAllocations[0].keyWorkerNote
  contractedAssistanceHoursPerWeek: number // flattened (converted from contractedAssistanceMinutesPerWeek) // TODO: minutes instead (rename)
  legalCapacity: string
  legalCapacityNote: string
  needsStrongAssistant: boolean
  cooperationStartDate: string // Y-M-D, flattened from active clientAllocations[0].cooperationStartDate
  needsAdvancedAssistant: boolean
  deactivatedAt: Date // flattened from active clientAllocations[0].deactivatedAt
  deactivationReason: string // flattened from active clientAllocations[0].deactivationReason
  targetGroupOkSystemCode: number
  clientAllocations: ClientAllocation[]

  user: User

  static sortByName(a: Client, b: Client) {
    return Client.intlSortComparer(a.user.lastName + ' ' + a.user.firstName, b.user.lastName + ' ' + b.user.firstName)
  }

  get title(): string {
    return this.user.title
  }

  get birthDate(): string | Date {
    return this.user.birthDate
  }

  get genderId(): number {
    return this.user.genderId
  }

  get idCardNumber(): string {
    return this.user.idCardNumber
  }

  get nationalityId(): number {
    return this.user.nationalityId
  }

  get maritalStatusId(): number {
    return this.user.maritalStatusId
  }

  get address_street(): string {
    return this.user.address_street
  }

  get address_houseNumber(): string {
    return this.user.address_houseNumber
  }

  get address_city(): string {
    return this.user.address_city
  }

  get address_postcode(): string {
    return this.user.address_postcode
  }

  get address_orp(): string {
    return this.user.address_orp
  }

  get keyWorkerNote() {
    return this.coordinatorNote
  }

  get contractedAssistanceMinutesPerWeek() {
    return this.clientAllocations?.[0]?.contractedAssistanceMinutesPerWeek
  }

  set title(title: string) {
    this.user.title = title
  }

  set birthDate(birthDate: string | Date) {
    this.user.birthDate = birthDate
  }

  set genderId(genderId: number) {
    this.user.genderId = genderId
  }

  set idCardNumber(idCardNumber: string) {
    this.user.idCardNumber = idCardNumber
  }

  set nationalityId(nationalityId: number) {
    this.user.nationalityId = nationalityId
  }

  set maritalStatusId(maritalStatusId: number) {
    this.user.maritalStatusId = maritalStatusId
  }

  set address_street(address_street: string) {
    this.user.address_street = address_street
  }

  set address_houseNumber(address_houseNumber: string) {
    this.user.address_houseNumber = address_houseNumber
  }

  set address_city(address_city: string) {
    this.user.address_city = address_city
  }

  set address_postcode(address_postcode: string) {
    this.user.address_postcode = address_postcode
  }

  set address_orp(address_orp: string) {
    this.user.address_orp = address_orp
  }

  set keyWorkerNote(value: string) {
     this.coordinatorNote = value
  }

  turnSubresourcesIntoFrontendModels() {
    this.user = this.user ? User.createFromProps(User, this.user) : this.user
    this.clientAllocations = (this.clientAllocations ?? []).map((a) =>
      ClientAllocation.createFromProps(ClientAllocation, a),
    )
  }

  isDeactivated() {
    return DateUtils.canBeParsedToDate(this.clientAllocations?.[0]?.deactivatedAt ?? this.deactivatedAt)
  }

  get fullname(): string {
    return this.user.fullName()
  }

  updateFromProps(props): this {
    super.updateFromProps(props)
    this.flattenClientAllocationArrayFields()
    return this
  }

  /**
   * Flattens the fields of the first object from the `clientAllocations` array onto the current instance.
   * Client always has only one client_allocation on the frontend since it is dependent on the currently chosen active allocation, so we flatten the fields onto the instance itself to avoid working with the array.
   */
  private flattenClientAllocationArrayFields() {
    const alloc = this.clientAllocations?.[0]
    if (alloc) {
      this.keyWorkerUserId = alloc.keyWorkerUserId
      this.keyWorkerUser = alloc.keyWorkerUser ? User.createFromProps(User, alloc.keyWorkerUser as User) : null
      this.coordinatorNote = alloc.keyWorkerNote
      this.cooperationStartDate = alloc.cooperationStartDate
      this.deactivatedAt = alloc.deactivatedAt
      this.deactivationReason = alloc.deactivationReason
    }
  }

  dehydrate<T extends this>(...omit): Partial<T> {
    // Omit fields that were moved to client_allocations — they are saved separately.
    const allocationFields: (keyof this)[] = [
      'keyWorkerUserId',
      'keyWorkerUser',
      'keyWorkerNote' as keyof this,
      'coordinatorNote',
      'cooperationStartDate',
      'contractedAssistanceHoursPerWeek',
      'deactivatedAt',
      'deactivationReason',
    ]
    return super.dehydrate(...([...omit, ...allocationFields] as (keyof this)[]))
  }
}
